import { io } from 'socket.io-client';
import config from '@/config/config';
import store from '@/redux/store';
import {
  addMessage,
  updateMessage,
  deleteMessage as deleteMessageAction,
  addTypingUser,
  removeTypingUser,
} from '@/redux/slices/messagesSlice';
import {
  updateGroup,
  removeGroup,
  updateGroupUnread,
} from '@/redux/slices/groupsSlice';
import {
  addNotification as addNotificationAction,
  setUnreadCount as setNotifUnreadCount,
} from '@/redux/slices/notificationsSlice';

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.vehicleTracking = {
      isActive: false,
      vehicleId: null,
      locationUpdateInterval: null,
      currentPosition: null,
      listeners: new Map()
    };
  }

  /**
   * Connect to Socket.IO server with JWT authentication
   */
  connect(token) {
    if (this.socket?.connected) return;

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    const SOCKET_URL = config.SOCKET_URL;

    this.socket = io(SOCKET_URL, {
      auth: {
        token: token, // JWT token for authentication
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    this.setupEventListeners();
  }

  /**
   * Setup all socket event listeners
   */
  setupEventListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.connected = true;
    });

    this.socket.on('disconnect', () => {
      this.connected = false;
    });

    this.socket.on('connect_error', () => {
      this.connected = false;
    });

    // Message events
    this.socket.on('new_message', (data) => {
      store.dispatch(addMessage({
        groupId: data.groupId,
        message: data
      }));
    });

    this.socket.on('message_deleted', (data) => {
      store.dispatch(deleteMessageAction({
        groupId: data.groupId,
        messageId: data.messageId
      }));
    });

    this.socket.on('message_read_receipt', (data) => {
      store.dispatch(updateMessage({
        groupId: data.groupId,
        messageId: data.messageId,
        updates: { isRead: true }
      }));
    });

    // Typing indicators
    this.socket.on('user_typing', (data) => {
      store.dispatch(addTypingUser({
        groupId: data.groupId,
        userId: data.userId
      }));

      // Auto-remove after 3 seconds
      setTimeout(() => {
        store.dispatch(removeTypingUser({
          groupId: data.groupId,
          userId: data.userId
        }));
      }, 3000);
    });

    this.socket.on('user_stop_typing', (data) => {
      store.dispatch(removeTypingUser({
        groupId: data.groupId,
        userId: data.userId
      }));
    });

    // Group events
    this.socket.on('group_updated', (data) => {
      store.dispatch(updateGroup(data.group));
    });

    this.socket.on('group_deleted', (data) => {
      store.dispatch(removeGroup(data.groupId));
    });

    this.socket.on('member_added', (data) => {
      // Optionally refresh group details
    });

    this.socket.on('member_removed', (data) => {
      // Optionally refresh group details
    });

    this.socket.on('admin_promoted', (data) => {
      // Optionally refresh group details
    });

    this.socket.on('admin_demoted', (data) => {
      // Optionally refresh group details
    });

    this.socket.on('member_joined', (data) => {
      // Optionally refresh group details
    });

    // Notification events (real-time)
    this.socket.on('new_notification', (data) => {
      store.dispatch(addNotificationAction({
        _id: data._id || `temp_${Date.now()}`,
        type: data.type,
        title: data.title,
        body: data.body,
        clickAction: data.clickAction,
        metadata: data,
        isRead: false,
        createdAt: data.createdAt || new Date().toISOString(),
      }));
    });

    // Vehicle tracking events
    this.socket.on('vehicle-joined', (data) => {
      this.vehicleTracking.vehicleId = data.vehicleId;
      this.emitVehicleEvent('vehicle-joined', data);
    });

    this.socket.on('location-updated', (data) => {
      this.vehicleTracking.currentPosition = data.location;
      this.emitVehicleEvent('location-updated', data);
    });

    this.socket.on('location-saved', (data) => {
      this.emitVehicleEvent('location-saved', data);
    });

    this.socket.on('vehicle-error', (data) => {
      this.emitVehicleEvent('vehicle-error', data);
    });

  }

  /**
   * Join a group room
   */
  joinGroup(groupId) {
    if (this.socket?.connected) {
      this.socket.emit('join_group', { groupId });
    }
  }

  /**
   * Leave a group room
   */
  leaveGroup(groupId) {
    if (this.socket?.connected) {
      this.socket.emit('leave_group', { groupId });
    }
  }

  /**
   * Send a message
   */
  sendMessage(groupId, content, messageType = 'text', attachments = [], replyTo = null) {
    if (this.socket?.connected) {
      this.socket.emit('send_message', {
        groupId,
        content,
        messageType,
        attachments,
        replyTo
      });
    } else {
      throw new Error('Socket not connected');
    }
  }

  /**
   * Delete a message
   */
  deleteMessage(messageId, groupId) {
    if (this.socket?.connected) {
      this.socket.emit('delete_message', { messageId, groupId });
    }
  }

  /**
   * Mark message as read
   */
  markMessageAsRead(messageId, groupId) {
    if (this.socket?.connected) {
      this.socket.emit('message_read', { messageId, groupId });
    }
  }

  /**
   * Send typing indicator
   */
  sendTyping(groupId) {
    if (this.socket?.connected) {
      this.socket.emit('typing', { groupId });
    }
  }

  /**
   * Send stop typing indicator
   */
  sendStopTyping(groupId) {
    if (this.socket?.connected) {
      this.socket.emit('stop_typing', { groupId });
    }
  }

  /**
   * Disconnect from socket server
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
    }
  }

  /**
   * Check if socket is connected
   */
  isConnected() {
    return this.connected && this.socket?.connected;
  }

  /**
   * Get detailed connection status for debugging
   */
  getConnectionStatus() {
    return {
      connected: this.connected,
      socketExists: !!this.socket,
      socketConnected: this.socket?.connected,
      socketId: this.socket?.id || null,
    };
  }

  /**
   * Add custom event listener
   */
  on(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  /**
   * Remove custom event listener
   */
  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  /**
   * Emit custom event
   */
  emit(event, data) {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    }
  }

  // ===== VEHICLE TRACKING METHODS =====

  /**
   * Start vehicle live tracking
   * @param {string} vehicleId - Vehicle ID to track
   */
  startVehicleTracking(vehicleId) {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      return false;
    }

    this.vehicleTracking.vehicleId = vehicleId;
    this.vehicleTracking.isActive = true;

    // Join vehicle tracking room
    this.socket.emit('join-vehicle', {
      vehicleId: vehicleId,
      userId: 'current-user'
    });

    // Start location tracking
    this.startLocationTracking();
    
    console.log(`Started vehicle tracking for: ${vehicleId}`);
    return true;
  }

  /**
   * Stop vehicle tracking
   */
  stopVehicleTracking() {
    this.vehicleTracking.isActive = false;
    this.vehicleTracking.vehicleId = null;
    
    if (this.vehicleTracking.locationUpdateInterval !== null) {
      if (navigator.geolocation) {
        navigator.geolocation.clearWatch(this.vehicleTracking.locationUpdateInterval);
      }
      this.vehicleTracking.locationUpdateInterval = null;
    }

    console.log('Stopped vehicle tracking');
  }

  /**
   * Start continuous GPS location tracking using watchPosition
   */
  startLocationTracking() {
    if (this.vehicleTracking.locationUpdateInterval !== null) {
      if (navigator.geolocation) {
        navigator.geolocation.clearWatch(this.vehicleTracking.locationUpdateInterval);
      }
      this.vehicleTracking.locationUpdateInterval = null;
    }

    if (!navigator.geolocation || !this.vehicleTracking.isActive) {
      return;
    }

    // Set up native browser location watcher
    this.vehicleTracking.locationUpdateInterval = navigator.geolocation.watchPosition(
      (position) => {
        if (!this.vehicleTracking.isActive) return;

        const locationData = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          speed: position.coords.speed,
          heading: position.coords.heading,
          altitude: position.coords.altitude,
        };

        this.vehicleTracking.currentPosition = locationData;
        this.sendLocationUpdate(locationData);
      },
      (error) => {
        if (!this.vehicleTracking.isActive) return;

        const errorMsg = {
          code: error.code,
          message: error.message || 'Unknown geolocation error',
        };
        console.error('Geolocation error:', errorMsg);
        this.emitVehicleEvent('geolocation-error', errorMsg);
      },
      {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 10000
      }
    );

    console.log('Started native GPS location tracking via watchPosition');
  }

  /**
   * Get current GPS location manually (one-off) and send to server
   */
  getCurrentLocation() {
    if (!navigator.geolocation || !this.vehicleTracking.isActive) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const locationData = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          speed: position.coords.speed,
          heading: position.coords.heading,
          altitude: position.coords.altitude,
        };

        this.vehicleTracking.currentPosition = locationData;
        this.sendLocationUpdate(locationData);
      },
      (error) => {
        const errorMsg = {
          code: error.code,
          message: error.message || 'Unknown geolocation error',
        };
        console.error('Geolocation error:', errorMsg);
        this.emitVehicleEvent('geolocation-error', errorMsg);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }

  /**
   * Send location update to server
   * @param {Object} location - Location data
   */
  sendLocationUpdate(location) {
    if (!this.socket?.connected || !this.vehicleTracking.vehicleId) {
      return;
    }

    const locationUpdate = {
      vehicleId: this.vehicleTracking.vehicleId,
      location,
      timestamp: new Date().toISOString()
    };

    this.socket.emit('location-update', locationUpdate);
    this.emitVehicleEvent('location-update-sent', locationUpdate);
  }

  /**
   * Add vehicle tracking event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  onVehicleEvent(event, callback) {
    if (!this.vehicleTracking.listeners.has(event)) {
      this.vehicleTracking.listeners.set(event, []);
    }
    this.vehicleTracking.listeners.get(event).push(callback);
  }

  /**
   * Remove vehicle tracking event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  offVehicleEvent(event, callback) {
    const callbacks = this.vehicleTracking.listeners.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Emit vehicle tracking event to listeners
   * @param {string} event - Event name
   * @param {any} data - Event data
   */
  emitVehicleEvent(event, data) {
    const callbacks = this.vehicleTracking.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in vehicle ${event} callback:`, error);
        }
      });
    }
  }

  /**
   * Get vehicle tracking status
   */
  getVehicleTrackingStatus() {
    return {
      isActive: this.vehicleTracking.isActive,
      vehicleId: this.vehicleTracking.vehicleId,
      currentPosition: this.vehicleTracking.currentPosition,
      isConnected: this.connected
    };
  }

  /**
   * Force location update now
   */
  updateLocationNow() {
    if (this.vehicleTracking.isActive) {
      this.getCurrentLocation();
    }
  }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;
